import { booleanAttribute, DestroyRef, Directive, DoCheck, ElementRef, inject, Input, OnChanges, Renderer2, SimpleChange } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, of, switchMap } from 'rxjs';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { GapAccessService } from '../services/gap-access/gap-access.service';
import { GapOperation } from './gap-permissions';

/**
 * A control running a GAP operation, locked while the user's level does not allow it: `aria-disabled`, a lock before
 * its label, the `gap-locked` class (drawn as disabled), the reason on hover and on focus; its clicks and keys do
 * nothing. A locked button stays focusable, so that a keyboard reaches its reason. It follows the level as it changes.
 * Its own reasons to be disabled stay on `disabled`, which disables it for good:
 *
 *     <button nz-button gapRequires="ltp:PUT /v1/input/{client_id}" [disabled]="!isDirty" (click)="save()">Save Changes</button>
 *
 * On a button, an `nz-menu-item`, or any clickable element (a table row: the lock goes in its first cell). It brings
 * its own `nz-tooltip`: the element must not have one; its own tooltip goes on `gapTooltip`, shown while it is not
 * locked. `null` requires nothing.
 */
@Directive({
  selector: '[gapRequires]',
  hostDirectives: [{ directive: NzTooltipDirective, inputs: ['nzTooltipPlacement'] }],
  host: {
    '[class.gap-locked]': 'reason !== null',
    '[attr.aria-disabled]': 'reason !== null || null',
    '[attr.aria-description]': 'reason',
    '(focusin)': 'reason !== null && showReason()',
    '(focusout)': 'hideReason()'
  }
})
export class GapRequiresDirective implements OnChanges, DoCheck {
  @Input({ required: true }) gapRequires: GapOperation | null = null;
  /** The control's own reasons to be disabled, besides the level. */
  @Input({ transform: booleanAttribute }) disabled = false;
  /** The control's own tooltip, shown while it is not locked (the lock's reason replaces it). */
  @Input() gapTooltip: string | null = null;

  /** Why the user may not run the operation; null when they may. */
  reason: string | null = null;

  private readonly _element: HTMLElement = inject(ElementRef).nativeElement;
  private readonly _renderer = inject(Renderer2);
  private readonly _tooltip = inject(NzTooltipDirective, { self: true });
  private readonly _button = inject(NzButtonComponent, { self: true, optional: true });
  private readonly _menuItem = inject(NzMenuItemComponent, { self: true, optional: true });
  private readonly _operation = new BehaviorSubject<GapOperation | null>(null);
  private _lock: HTMLElement | null = null;

  constructor() {
    const gapAccessService = inject(GapAccessService);
    this._operation
      .pipe(
        switchMap(operation => (operation ? gapAccessService.denied$(operation) : of(null))),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe(reason => this.lock(reason));
    // Before Angular's own listeners: a locked control runs nothing, even where it stays focusable (a row, a menu item)
    const block = (event: Event) => {
      if (this.reason !== null && (event.type === 'click' || isActivation(event))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    this._element.addEventListener('click', block, true);
    this._element.addEventListener('keydown', block, true);
  }

  ngOnChanges(): void {
    if (this._operation.value !== this.gapRequires) {
      this._operation.next(this.gapRequires);
    }
    this.showTitle();
  }

  /**
   * Disables the control for its own reasons, after the template set its inputs: a button's `[disabled]` comes here,
   * not to the button; ng-zorro's controls draw their disabled state from their own input. A lock is not one of them:
   * a locked button stays focusable.
   */
  ngDoCheck(): void {
    const locked = this.reason !== null;
    if (this._element instanceof HTMLButtonElement) {
      this._element.disabled = this.disabled;
    }
    if (this._button) {
      this._button.disabled = this.disabled;
    }
    if (this._menuItem) {
      this._menuItem.nzDisabled = locked;
    }
  }

  /** The reason, shown as on hover when a keyboard reaches the locked control. */
  showReason(): void {
    this._tooltip.show();
  }

  hideReason(): void {
    this._tooltip.hide();
  }

  private lock(reason: string | null): void {
    this.reason = reason;
    this.showTitle();
    if (reason !== null && !this._lock) {
      this._lock = this._renderer.createElement('span');
      this._renderer.addClass(this._lock, 'gap-lock');
      this._renderer.setAttribute(this._lock, 'aria-hidden', 'true');
      const target = this._element instanceof HTMLTableRowElement ? (this._element.cells[0] ?? this._element) : this._element;
      this._renderer.insertBefore(target, this._lock, target.firstChild);
    } else if (reason === null && this._lock) {
      this._lock.remove();
      this._lock = null;
    }
    this.ngDoCheck();
  }

  /** The tooltip: the lock's reason, else the control's own. */
  private showTitle(): void {
    const title = this.reason ?? this.gapTooltip;
    const previous = this._tooltip.title;
    if (title !== previous) {
      this._tooltip.title = title;
      this._tooltip.ngOnChanges({ title: new SimpleChange(previous, title, false) });
    }
  }
}

/** Enter or Space: the keys that run a control. */
function isActivation(event: Event): boolean {
  return event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ');
}
